import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, forwardRef } from "@angular/core";
import { FormControl, NG_VALUE_ACCESSOR } from "@angular/forms";
import { BaseFormControlComponent } from "apps-module/app/shared-library/form-controls/base-form-control.component";
import { DestroyableMixin } from "apps-module/app/shared-library/mixins/destroyable.mixin";
import { ApiRequestDispatcherService } from "apps-module/app/shared-library/services/api/api-request-dispatcher.service";
import { shortKey } from "apps-module/app/shared-library/utils/common.utils";
import { SmartSuggestService } from "core-library/components/smart-suggest/smart-suggest.service";
import { SearchResponseModel } from "core-library/components/smart-suggest/suggest-response.model";
import { debounceTime, map, takeUntil } from "rxjs";

@Component({
    selector: "[pcg-ng-search-bar]",
    templateUrl: "./search-bar.component.html",
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchBarComponent), multi: true }
    ]
})
export class SearchBarComponent extends DestroyableMixin(BaseFormControlComponent<string>) implements OnInit {

    public id: string = null;
    public suggestionsHided: boolean = true;
    public searchTextControl: FormControl = new FormControl("");

    @ViewChild("suggestionsContainer")
    public suggestionsContainer: ElementRef<HTMLElement>;

    @Input()
    public useSuggestions: boolean = false;

    @Input()
    public apiUrl: string = null;

    @Input()
    public searchPageUrl: string = null;

    @Input()
    public homePageId: string = null;

    @Output()
    public onSubmit = new EventEmitter();

    public constructor(private readonly api: ApiRequestDispatcherService) {
        super();

        this.searchTextControl.valueChanges
            .pipe(
                map((value: string) => this.writeNewValue(value)),
                debounceTime(700),
                takeUntil(this.onDestroy)
            )
            .subscribe(() => this.setSuggestions());
    }

    public onFocusIn(): void {
        this.setSuggestions();
    }

    public onFocusOut(): void {
        setTimeout(() => this.clearSuggestions(), 150);
    }

    public writeValue(value: any): void {
        super.writeValue(value);
        this.searchTextControl.setValue(value, { emitEvent: false });
    }

    private setSuggestions(): void {
        const searchText = this.value;

        if (searchText.length <= 2) {
            return;
        }

        if (!this.suggestionsHided) {
            return;
        }

        this.suggestionsHided = false;

        this.api.get<SearchResponseModel>(`${this.apiUrl}?&homePageId=${this.homePageId}&searchText=${searchText}`)
            .subscribe(response => {
                this.clearSuggestions();

                SmartSuggestService.getSuggestionElements(response.recommendations, "Recommended Links")
                    .forEach((element: string) => this.suggestionsContainer.nativeElement.innerHTML += element);

                SmartSuggestService.getSuggestionElements(response.suggestions, "Content / Articles")
                    .forEach((element: string) => this.suggestionsContainer.nativeElement.innerHTML += element);

                this.suggestionsContainer.nativeElement.innerHTML += SmartSuggestService.getShowMoreElement(response.showMoreCount, searchText, this.searchPageUrl);
            });
    }

    private clearSuggestions(): void {
        this.suggestionsContainer.nativeElement.innerHTML = "";
        this.suggestionsHided = true;
    }

    public ngOnInit(): void {
        this.id = `searchText-${shortKey()}`;
    }

    public submit(): void {
        this.onSubmit.emit();
        this.clearSuggestions();
    }

    public onInputClick(): void {
        this.setSuggestions();
    }
}
